import { useState } from 'react';

interface NavbarProps {
  isScrolled: boolean;
}

export default function Navbar({ isScrolled }: NavbarProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white/90 backdrop-blur-xl shadow-lg border-b border-gray-200/50'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Neutral Logo */}
          <button
            onClick={() => scrollToSection('home')}
            className="flex items-center gap-2.5 cursor-pointer group"
          >
            <div
              className={`w-9 h-9 md:w-10 md:h-10 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:scale-110 ${
                isScrolled
                  ? 'bg-gradient-to-br from-teal-500 to-emerald-600'
                  : 'bg-white/15 backdrop-blur-md border border-white/20'
              }`}
            >
              <i className="ri-settings-3-line text-white text-lg md:text-xl"></i>
            </div>
            <div className="flex flex-col">
              <span
                className={`font-oswald text-base md:text-lg font-bold leading-tight transition-colors duration-300 uppercase tracking-wide ${
                  isScrolled ? 'text-gray-900' : 'text-white'
                }`}
              >
                serv1ce.kz
              </span>
              <span
                className={`text-[10px] md:text-xs font-medium leading-tight transition-colors duration-300 ${
                  isScrolled ? 'text-teal-600' : 'text-teal-300'
                }`}
              >
                &nbsp;Лучший сервис в Алматы
              </span>
            </div>
          </button>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center space-x-6 xl:space-x-8">
            {[
              { id: 'home', label: 'Главная' },
              { id: 'services', label: 'Услуги' },
              { id: 'prices', label: 'Цены' },
              { id: 'how-we-work', label: 'Как мы работаем' },
              { id: 'reviews', label: 'Отзывы' },
              { id: 'contact', label: 'Контакты' },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`text-sm font-medium transition-all duration-300 whitespace-nowrap hover:scale-105 cursor-pointer relative group ${
                  isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white/90 hover:text-white'
                }`}
              >
                {item.label}
                <span
                  className={`absolute -bottom-1 left-0 h-0.5 w-0 group-hover:w-full transition-all duration-300 ${
                    isScrolled ? 'bg-teal-500' : 'bg-teal-300'
                  }`}
                />
              </button>
            ))}
            <a
              href="tel:+77074325020"
              className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-5 py-2.5 rounded-full text-sm font-medium hover:from-teal-600 hover:to-emerald-600 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill mr-1.5"></i>
              +7 (707) 432-50-20
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-all duration-300 hover:scale-110 cursor-pointer"
          >
            <i
              className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl transition-all duration-300 ${
                isScrolled ? 'text-gray-900' : 'text-white'
              }`}
            ></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-400 ${
          isMobileMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-white/95 backdrop-blur-xl shadow-xl border-t border-gray-100">
          <div className="px-4 py-3 space-y-1">
            {[
              { id: 'home', label: 'Главная', icon: 'ri-home-4-line' },
              { id: 'services', label: 'Услуги', icon: 'ri-tools-line' },
              { id: 'prices', label: 'Цены', icon: 'ri-price-tag-3-line' },
              { id: 'how-we-work', label: 'Как мы работаем', icon: 'ri-flow-chart-line' },
              { id: 'reviews', label: 'Отзывы', icon: 'ri-star-line' },
              { id: 'contact', label: 'Контакты', icon: 'ri-mail-line' },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="flex items-center gap-3 w-full text-left px-4 py-3 text-sm font-medium text-gray-700 hover:bg-teal-50 hover:text-teal-600 rounded-xl whitespace-nowrap transition-all duration-300 cursor-pointer"
              >
                <i
                  className={`${item.icon} text-lg w-5 h-5 flex items-center justify-center text-teal-500`}
                ></i>
                {item.label}
              </button>
            ))}
            <div className="pt-2 pb-1">
              <a
                href="tel:+77074325020"
                className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-6 py-3 rounded-xl text-sm font-semibold shadow-lg whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-fill"></i>
                +7 (707) 432-50-20
              </a>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
